import React, { useState, useEffect } from 'react';
import './AdminRegistrationOverlay.css';
import TermsOverlay from '../terms/TermsOverlay';

interface AdminRegistrationOverlayProps {
    selectedPlan: string;
    billingCycle: 'monthly' | 'yearly';
    planPrice: number;
    vehicleCount: number;
    setVehicleCount: (count: number) => void;
    onClose: () => void;
    onRegister: () => void;
    isLoggedIn?: boolean;
    currentVehicleCount?: number;
}

// Plan feature data
const planFeatures: Record<string, { features: string[]; color: string }> = {
    '베이직': {
        color: '#475569',
        features: [
            '차량 상태 및 배터리 SoH 모니터링',
            '주행 기록 및 충전 이력 자동 관리',
            '배터리 교체 / 점검 알림',
            '운행 일지 자동 생성',
        ]
    },
    '스탠다드': {
        color: '#0aa096',
        features: [
            'Basic 기능 전체 포함',
            '실시간 배터리 관제 및 모니터링',
            '일·주간 운행 데이터 리포트',
            '운영 효율 개선 리포트',
        ]
    },
    '프리미엄': {
        color: '#dc2626',
        features: [
            'Standard 기능 전체 포함',
            '고객사 전용 리포트 대시보드',
            '클라우드 서버 무제한 접근',
            '전담 매니저 배정',
        ]
    },
    '프리미엄+TCO(선불)': {
        color: '#2563eb',
        features: [
            'Premium 기능 전체 포함',
            '재제조 배터리 교체 서비스',
            '부품 교체 서비스 포함',
            '전기 물류차 특화 관리',
        ]
    },
    '프리미엄+TCO(후불)': {
        color: '#4f46e5',
        features: [
            'Premium 기능 전체 포함',
            '재제조 배터리 교체 서비스',
            '부품 교체 서비스 포함',
            '전기 물류차 특화 관리',
        ]
    }
};

export default function AdminRegistrationOverlay({
    selectedPlan,
    billingCycle,
    planPrice,
    vehicleCount,
    setVehicleCount,
    onClose,
    onRegister,
    isLoggedIn = false,
    currentVehicleCount = 0
}: AdminRegistrationOverlayProps) {
    const [formData, setFormData] = useState({
        accountId: '',
        password: '',
        passwordConfirm: '',
        businessNumber: '',
        companyName: '',
        adminName: '',
        adminPhone: '',
        emailDomain: ''
    });

    const [step, setStep] = useState<1 | 2>(1);

    // 이메일 인증 상태 추가
    const [isEmailVerificationSent, setIsEmailVerificationSent] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');

    const [termsModal, setTermsModal] = useState<'service' | 'location' | 'privacy' | null>(null);

    // Number formatting helpers
    const formatPhoneNumber = (value: string) => {
        const numbers = value.replace(/[^\d]/g, '').slice(0, 11);
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    };

    const formatBusinessNumber = (value: string) => {
        const numbers = value.replace(/[^\d]/g, '').slice(0, 10);
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        let finalValue = value;
        if (name === 'adminPhone') {
            finalValue = formatPhoneNumber(value);
        } else if (name === 'businessNumber') {
            finalValue = formatBusinessNumber(value);
        }
        
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isEmailVerified) {
            alert('이메일 인증을 완료해주세요.');
            return;
        }
        if (formData.password !== formData.passwordConfirm) {
            alert('비밀번호가 일치하지 않습니다.');
            return;
        }
        setStep(2);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onRegister();
    };

    const monthlyTotal = planPrice * vehicleCount;
    const finalTotal = billingCycle === 'yearly' ? monthlyTotal * 12 : monthlyTotal;

    return (
        <div className="admin-reg-overlay">
            <div className="admin-reg-container">
                
                {/* 1. LEFT PANEL */}
                <div className="admin-reg-left-panel">
                    <div className="left-panel-logo">
                        <img src="/assets/images/logos/eview-logo.png" alt="EView Logo" />
                    </div>
                    
                    <div className="left-panel-hero">
                        <h2>EV Fleet<br/>통합관리 솔루션</h2>
                        <p>EView를 통해 차량 관리의<br/>혁신을 시작하세요.</p>
                    </div>
                    
                    <div className="receipt-card">
                        <h4>{isLoggedIn ? '추가 결제 (Add-on) 요약' : '주문 요약'}</h4>
                        <div className="receipt-row">
                            <span>{isLoggedIn ? '이용 중인 플랜' : '선택 요금제'}</span>
                            <strong>{selectedPlan}</strong>
                        </div>
                        <div className="receipt-row">
                            <span>결제 주기</span>
                            <strong>{billingCycle === 'monthly' ? '월간' : '연간'}</strong>
                        </div>
                        <div className="receipt-row">
                            <span>적용 예상 대수</span>
                            <strong>{vehicleCount}대</strong>
                        </div>
                        <div className="receipt-divider"></div>
                        <div className="receipt-total">
                            <span className="label">총 예상 금액</span>
                            <span className="amount">₩{finalTotal.toLocaleString()}</span>
                        </div>
                    </div>
                    
                    <div className="left-panel-trust">
                        <div className="trust-item">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                            <span>업계 최고 수준의 <strong>보안 아키텍처</strong> 적용</span>
                        </div>
                        <div className="trust-item">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                            <span>도입 문의부터 <strong>전담 매니저 배정</strong></span>
                        </div>
                    </div>
                </div>

                {/* 2. RIGHT PANEL */}
                <div className="admin-reg-right-panel">
                    <button className="btn-close-modal" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                    
                    {!isLoggedIn && (
                        <div className="stepper-header">
                            <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>1. 계정 생성</div>
                            <div className="step-line"></div>
                            <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>2. 기업 정보</div>
                        </div>
                    )}

                    <div className="form-scroll-area">
                        {isLoggedIn ? (
                            <form className="admin-form" onSubmit={handleSubmit}>
                                <div className="form-intro">
                                    <h3>차량 대수 추가 구매</h3>
                                    <p>현재 이용 중인 요금제에 추가할 차량 대수를 선택해 주세요.<br/><span style={{ fontSize: '13px', color: '#64748b' }}>* 별도의 회사 및 계정 등록 절차가 생략됩니다.</span></p>
                                </div>
                                <div className="form-group" style={{ marginTop: '24px' }}>
                                    <h4 className="form-group-title">추가 수량 선택<span className="req">*</span></h4>
                                    <div className="vehicle-count-picker">
                                        <button type="button" className="count-btn" onClick={() => setVehicleCount(Math.max(1, vehicleCount - 1))}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                        </button>
                                        <div className="count-display" style={{ color: '#2563eb' }}>
                                            +{vehicleCount}<span className="count-unit">대 추가</span>
                                        </div>
                                        <button type="button" className="count-btn" onClick={() => setVehicleCount(vehicleCount + 1)}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                        </button>
                                    </div>
                                    <div style={{ marginTop: '12px', fontSize: '13px', color: '#475569', textAlign: 'center', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        현재 운용 대수: <strong>{currentVehicleCount}대</strong> <span style={{ margin: '0 8px', color: '#cbd5e1' }}>➔</span> 결제 후 총 대수: <strong style={{color: '#2563eb', fontSize: '14px'}}>{currentVehicleCount + vehicleCount}대</strong>
                                    </div>
                                </div>
                                <div className="form-actions-row" style={{ marginTop: '40px' }}>
                                    <button type="submit" className="btn-submit flex-1" style={{ fontSize: '16px', padding: '14px' }}>추가 결제 진행</button>
                                </div>
                            </form>
                        ) : step === 1 ? (
                            <form className="admin-form" onSubmit={handleNext}>
                                <div className="form-intro">
                                    <h3>관리자 계정 생성</h3>
                                    <p>EView 대시보드 최고 관리자 계정을 설정합니다.</p>
                                </div>
                                
                                <div className="form-group">
                                    <div className="field">
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                        <input type="text" name="adminName" placeholder="이름" value={formData.adminName} onChange={handleChange} required />
                                    </div>

                                    <div className="field-row">
                                        <div className="field flex-1" style={{ marginBottom: 0 }}>
                                            <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                                            <input type="email" name="accountId" placeholder="아이디 (사내 이메일)" value={formData.accountId} onChange={handleChange} required disabled={isEmailVerified} />
                                        </div>
                                        <button type="button" className={`btn-verify-side ${isEmailVerified ? 'verified' : ''}`} onClick={() => setIsEmailVerificationSent(true)} disabled={!formData.accountId || isEmailVerified}>
                                            {isEmailVerified ? '인증완료' : '인증메일 발송'}
                                        </button>
                                    </div>
                                    
                                    {isEmailVerificationSent && !isEmailVerified && (
                                        <div className="field-row" style={{ marginTop: '16px' }}>
                                            <div className="field flex-1" style={{ marginBottom: 0 }}>
                                                <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                                                <input type="text" placeholder="인증번호 6자리" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required disabled={isEmailVerified} />
                                            </div>
                                            <button type="button" className="btn-verify-side" onClick={() => {
                                                if (verificationCode.length === 6) {
                                                    setIsEmailVerified(true);
                                                } else {
                                                    alert('인증번호 6자리를 입력해주세요.');
                                                }
                                            }}>확인</button>
                                        </div>
                                    )}

                                    <div className="field" style={{ marginTop: '16px' }}>
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                        <input type="password" name="password" placeholder="비밀번호 영문, 숫자 조합 (10자 이상)" value={formData.password} onChange={handleChange} required />
                                        <button type="button" className="pwd-eye">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        </button>
                                    </div>
                                    <div className="field">
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                        <input type="password" name="passwordConfirm" placeholder="비밀번호 확인" value={formData.passwordConfirm} onChange={handleChange} required />
                                    </div>
                                    <div className="pwd-checks">
                                        <span className={`check ${formData.password.length >= 10 ? 'ok' : ''}`}>10자 이상</span>
                                        <span className={`check ${/[a-zA-Z]/.test(formData.password) ? 'ok' : ''}`}>영문</span>
                                        <span className={`check ${/\d/.test(formData.password) ? 'ok' : ''}`}>숫자</span>
                                        <span className="strength">
                                            <span className={`bar ${formData.password.length >= 6 ? 'filled' : ''}`}></span>
                                            <span className={`bar ${formData.password.length >= 10 ? 'filled' : ''}`}></span>
                                            <span className={`bar ${formData.password.length >= 12 ? 'filled' : ''}`}></span>
                                            <span className="label">
                                                {formData.password.length === 0 ? '입력 전' : formData.password.length < 10 ? '불가' : '안전함'}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                                <button type="submit" className="btn-submit" style={{ width: '100%' }}>다음 단계로 진행</button>
                            </form>
                        ) : (
                            <form className="admin-form" onSubmit={handleSubmit}>
                                <div className="form-intro">
                                    <h3>기업 및 서비스 정보</h3>
                                    <p>도입에 필요한 기본 회사 및 시스템 정보를 입력해주세요.</p>
                                </div>

                                {/* 서비스 대수 선택 */}
                                <div className="form-group">
                                    <h4 className="form-group-title">적용 서비스 대수<span className="req">*</span></h4>
                                    <div className="vehicle-count-picker">
                                        <button type="button" className="count-btn" onClick={() => setVehicleCount(Math.max(1, vehicleCount - 1))}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                        </button>
                                        <div className="count-display">
                                            {vehicleCount}<span className="count-unit">대</span>
                                        </div>
                                        <button type="button" className="count-btn" onClick={() => setVehicleCount(vehicleCount + 1)}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                        </button>
                                    </div>
                                </div>

                                {/* 회사 정보 */}
                                <div className="form-group">
                                    <h4 className="form-group-title">기업 본사 정보<span className="req">*</span></h4>
                                    <div className="field-row">
                                        <div className="field flex-1">
                                            <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                                            <input type="text" name="businessNumber" placeholder="사업자 번호 (10자리)" value={formData.businessNumber} onChange={handleChange} required />
                                        </div>
                                        <div className="field flex-1">
                                            <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                                            <input type="text" name="companyName" placeholder="회사명" value={formData.companyName} onChange={handleChange} required />
                                        </div>
                                    </div>
                                    <div className="field">
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                                        <input type="text" name="emailDomain" placeholder="전용 단축 도메인 (예: company.co.kr)" value={formData.emailDomain} onChange={handleChange} required />
                                    </div>
                                </div>

                                {/* 담당자 정보 */}
                                <div className="form-group">
                                    <h4 className="form-group-title">담당자 연락처<span className="req">*</span></h4>
                                    <div className="field-row">
                                        <div className="field flex-1">
                                            <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                            <input type="text" name="adminName" placeholder="담당자명" value={formData.adminName} onChange={handleChange} required />
                                        </div>
                                        <div className="field flex-1">
                                            <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                                            <input type="tel" name="adminPhone" placeholder="전화번호" value={formData.adminPhone} onChange={handleChange} required />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-actions-row">
                                    <button type="button" className="btn-back" onClick={() => setStep(1)}>이전</button>
                                    <button type="submit" className="btn-submit flex-1">가입 마치고 결제 진행</button>
                                </div>

                                <p className="terms">
                                    가입하면 <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('service'); }}>서비스 이용약관 ↗</a>과 <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('location'); }}>위치정보 이용약관 ↗</a>,<br />
                                    <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('privacy'); }}>개인정보 수집 및 이용 ↗</a>에 동의한 것으로 봅니다.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
            {termsModal && (
                <TermsOverlay type={termsModal} onClose={() => setTermsModal(null)} />
            )}

            <div className="login-side-content">
                <h2 className="side-subtitle">For EV Batteries</h2>
                <h1 className="side-title">Predictive diagnostics AI</h1>
            </div>
        </div>
    );
}
